import { useCreateNotification } from '../../api/aws/useCreateNotification'
import { useSlackNotification } from '../../api/slack/useSlackNotification'
import { useState, useCallback } from 'react'
const buildNotificationData = ({ task, subtask, field, isQueue: _isQueue }: { task: any; subtask: any; field: any; isQueue?: boolean }) => {
  const name = subtask?.name || 'Unnamed Subtask'
  const desc = subtask?.description || ''
  const due = subtask?.dueDate ? new Date(subtask.dueDate).toLocaleDateString() : null

  let title = 'Assigned to subtask'
  if (field === 'queue') title = 'Added as Active Assignee to subtask'
  else if (field === 'assigner') title = 'Marked as Assigner for subtask'
  else if (field === 'assignee') title = 'Added as Assignee to subtask'

  const envBaseUrl = import.meta.env.VITE_ENV === 'production' ? 'https://grobotic.app' : 'https://dev.grobotic.app'

  const subtaskUrl = `${envBaseUrl}/teamtasks/${task.PK}/${task.SK}/subtasks`
  const slackLines = [`• <${subtaskUrl}|*${name}*>`] // ← clickable bold link
  if (desc) slackLines.push(`> ${desc}`)
  if (due) slackLines.push(`🗓️ *Due:* ${due}`)

  return {
    notifyData: {
      title,
      type: 'subtask',
      board: 'teamtasks',
      url: `/teamtasks/${task.PK}/${task.SK}/subtasks`,
      message: `You’ve been marked as "${field}" for subtask "${name}"`,
    },
    slackHeader: title,
    slackMessageText: slackLines.join('\n'),
  }
}

export const useUserNotifications = () => {
  const { mutate: createNotification } = useCreateNotification()
  const { mutate: notifySlack } = useSlackNotification()
  const [_sortedNotifications, setSortedNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)

  const markAsRead = useCallback(notify_uuid => {
    setSortedNotifications(prev =>
      prev.map(notification =>
        notify_uuid.includes(notification.notify_uuid) ? { ...notification, isRead: true } : notification
      )
    )
    setUnreadCount(prev => Math.max(prev - 1, 0))
  }, [])

  const addNotification = useCallback(
    payload => {
      payload?.forEach(notification => {
        createNotification(notification, {
          onSuccess: data => {
            setSortedNotifications(prev => [...prev, data])
            setUnreadCount(prev => prev + 1)
          },
          onError: err => console.error('Error creating notification:', err),
        })
      })
    },
    [createNotification]
  )

  const convertHtmlToPlainText = html => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    doc.querySelectorAll('[data-type="mention"]').forEach(el => {
      const label = el.getAttribute('data-label') || ''
      el.replaceWith(doc.createTextNode(`@${label}`))
    })
    return doc.body.textContent || ''
  }
  const sendSlackNotifications = ({
    users = [],
    task,
    senderId,
    headerMessage = '',
    messageText = '',
    blocksBuilder, // optional override
    includeTaskLink = true,
    includeSender = true,
  }) => {
    if (!users.length || !task?.PK || !task?.SK) return

    const taskUrl =
      import.meta.env.VITE_ENV === 'production'
        ? `https://grobotic.app/teamtasks/${task.PK}/${task.SK}/main`
        : `https://dev.grobotic.app/teamtasks/${task.PK}/${task.SK}/main`

    const plainText = convertHtmlToPlainText(messageText)

    const getDefaultBlocks = (slackId, userHeader = '') => {
      const blocks = []

      if (headerMessage || userHeader) {
        blocks.push({
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `Hi <@${slackId}>, ${userHeader || headerMessage}`,
          },
        })
      }

      if (includeSender && senderId) {
        blocks.push({
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: `*Assigned by:* <@${senderId}>`,
            },
          ],
        })
      }

      if (plainText) {
        blocks.push(
          { type: 'divider' },
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: `*Message:* ${plainText}`,
            },
          }
        )
      }

      if (includeTaskLink) {
        blocks.push({
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*<${taskUrl}|View Task: ${task.name}>*`,
          },
        })
      }

      return blocks
    }

    users.forEach(user => {
      const slackId = user.id
      const blocks =
        typeof blocksBuilder === 'function'
          ? blocksBuilder({ user, task, senderId, taskUrl, plainText })
          : getDefaultBlocks(slackId, user.slackHeader)

      notifySlack({
        slackUserIds: [slackId],
        blocks,
        text: `You’ve been assigned to a new subtask in ${task.name}`,
        icon_emoji: ':bell:',
      })
    })
  }

  // const sendSlackNotifications = (users, headerMessage, messageText, task, senderId) => {
  //   if (!users?.length || !messageText || !task?.PK || !task?.SK) return
  // const taskUrl =
  //   import.meta.env.VITE_ENV === 'production'
  //     ? `https://grobotic.app/teamtasks/${task.PK}/${task.SK}/main`
  //     : `https://dev.grobotic.app/teamtasks/${task.PK}/${task.SK}/main`
  //   const plainText = convertHtmlToPlainText(messageText)

  //   const baseBlock = [
  //     {
  //       type: 'section',
  //       text: {
  //         type: 'mrkdwn',
  //         text: `Hi <@{SLACK_ID}>, ${headerMessage}\nTagged by: <@${senderId}>`,
  //       },
  //     },
  //     { type: 'divider' },
  //     {
  //       type: 'section',
  //       text: {
  //         type: 'mrkdwn',
  //         text: `*Message:* ${plainText}`,
  //       },
  //     },
  //     {
  //       type: 'section',
  //       text: {
  //         type: 'mrkdwn',
  //         text: `*<${taskUrl}|View Task: ${task.name}>*`,
  //       },
  //     },
  //   ]

  //   const slackUserIds = users.map(u => u.id).filter(Boolean)
  //   const blocksWithPlaceholder = JSON.stringify(baseBlock)

  //   slackUserIds.forEach(slackId => {
  //     const blocks = JSON.parse(blocksWithPlaceholder.replaceAll('{SLACK_ID}', slackId))

  //     notifySlack({
  //       slackUserIds: [slackId],
  //       blocks,
  //       text: `You've been tagged in ${task.name}`,
  //       icon_emoji: ':bell:',
  //     })
  //   })
  // }
  const notifyTaskAssignees = (
    userSubtaskMap, // [{ id, name, image_512, subtasks: [{ subtask, fieldChanged }] }]
    task
  ) => {
    if (!userSubtaskMap?.length || !task?.PK || !task?.SK) return

    const senderId = localStorage.getItem('slack_user_id')
    const allNotifications = []
    const slackUsers = []

    userSubtaskMap.forEach(({ id, name, image_512, subtasks }) => {
      const slackLines = []
      subtasks.forEach(({ subtask, fieldChanged }) => {
        const { notifyData, slackHeader, slackMessageText } = buildNotificationData({
          task,
          subtask,
          field: fieldChanged,
          isQueue: fieldChanged === 'queue',
        })

        // App Notification
        allNotifications.push({
          userId: id,
          ...notifyData,
          boardItemName: task.name,
          taskPK: task.PK,
          taskSK: task.SK,
          imageUrl: image_512 || '',
        })

        slackLines.push(slackMessageText)
      })

      slackUsers.push({
        id,
        taskName: task.name,
        slackHeader: `Assigned to subtasks in *${task.name}*`,
        slackText: slackLines.join('\n'),
      })
    })

    addNotification(allNotifications)

    sendSlackNotifications({
      users: slackUsers,
      task,
      senderId,
      blocksBuilder: ({ user, task, senderId, taskUrl }) => [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `Hi <@${user.id}>, ${user.slackHeader}`,
          },
        },
        {
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: `*Assigned by:* <@${senderId}>`, // ✅ Add this
            },
          ],
        },
        { type: 'divider' },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: user.slackText,
          },
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*<${taskUrl}|View Task: ${task.name}>*`,
          },
        },
      ],
    })
  }

  // const notifyTaskAssignees = (
  //   usersToNotify, // Array of user objects
  //   task, // The task object
  //   notifyData = {}, // { title, type, board, url, message }
  //   headerMessage = 'new notification',
  //   slackMessageText = '' // Optional rich text for Slack
  // ) => {
  //   console.log('[notifyTaskAssignees] slackMessageText', slackMessageText)
  //   if (!usersToNotify?.length || !task?.name || !task?.PK || !task?.SK) return
  //   const senderId = localStorage.getItem('slack_user_id')
  //   const notificationPayload = usersToNotify.map(user => ({
  //     userId: user.id,
  //     type: notifyData.type || 'Task',
  //     severity: notifyData.severity || 'success',
  //     title: notifyData.title || 'Assigned to Task',
  //     message: notifyData.message || '',
  //     board: notifyData.board || 'teamtasks',
  //     boardItemName: task.name,
  //     taskPK: task.PK,
  //     taskSK: task.SK,
  //     imageUrl: user.image_512 || '',
  //     url: notifyData.url || `/teamtasks/${task.PK}/${task.SK}/messages`,
  //   }))

  //   addNotification(notificationPayload)

  //   if (slackMessageText) {
  //     sendSlackNotifications(usersToNotify, headerMessage, slackMessageText, task, senderId)
  //   }
  // }
  const createMentionPayload = (
    userData,
    type,
    severity,
    title,
    message,
    board,
    boardItemName,
    boardId,
    imageUrl,
    url
  ) => {
    console.log('userData', userData, 'type', type, 'title', title, 'message', message)
    if (!(userData?.length > 0)) {
      console.log('no user data')
      return
    }
    return userData.map(user => {
      return {
        userId: user?.id,
        type: type,
        severity: severity,
        title: title,
        message: message,
        board: board || '',
        boardItemName: boardItemName,
        boardId: boardId,
        imageUrl: user?.image_512,
        url: url || '',
      }
    })
  }
  const createTaskMentionPayload = (
    userData,
    type,
    severity,
    title,
    message,
    board,
    boardItemName,
    taskPK,
    taskSK,
    imageUrl,
    url
  ) => {
    console.log('userData', userData, 'type', type, 'title', title, 'message', message, 'url', url)
    if (!(userData?.length > 0)) {
      console.log('no user data')
      return
    }
    return userData.map(user => {
      return {
        userId: user?.id,
        type: type,
        severity: severity,
        title: title,
        message: message,
        board: board || '',
        boardItemName: boardItemName,
        taskPK: taskPK,
        taskSK: taskSK,
        imageUrl: user?.image_512,
        url: url || '',
      }
    })
  }
  return {
    unreadCount,
    markAsRead,
    addNotification,
    notifyTaskAssignees,
    createMentionPayload,
    createTaskMentionPayload,
  }
}
